import React, { useEffect, useState } from 'react';

import { LOAD_CAREPLAN_LIST_DATA } from '../../Careplan/ListData/ListQueries';
import { GET_LATEST_DRAFT_NEXT_STEP } from './Queries';
import { CREATE_OR_UPDATE_NEXT_STEPS, SUBMIT_NEXT_STEPS } from './Mutations';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card,
  Box,
  Button,
  MenuItem,
  Stack,
  Typography,
  TextField,
  Snackbar,
  CircularProgress,
  IconButton,
  Fade,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { NextStepDropdown, ClearButton } from './NextStepsUtils';

import { filterListdata } from 'op-pages/RO/Careplan/ListData/ListData';

interface NextStepsProps {
  patientId: string;
  toggleShowWarningIcon: (show: boolean) => void;
}

const NextSteps: React.FC<NextStepsProps> = ({ patientId, toggleShowWarningIcon }) => {
  const [nextStepsOptions, setNextStepsOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedNextStep, setSelectedNextStep] = useState<string>('');
  const [nextAppointmentTypeOptions, setNextAppointmentTypeOptions] = useState<Array<{ label: string; value: string }>>(
    [],
  );
  const [selectedNextAppointmentType, setSelectedNextAppointmentType] = useState<string>('');
  const [nextAppointmentMethodOptions, setNextAppointmentMethodOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [selectedNextAppointmentMethod, setSelectedNextAppointmentMethod] = useState<string>('');
  const [nextStepTimingOptions, setNextStepTimingOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedNextStepTiming, setSelectedNextStepTiming] = useState<string>('');
  const [nextStepTimingInt, setNextStepTimingInt] = useState<string>('');
  const [nextStepComments, setNextStepComments] = useState<string>('');
  const [nextStepId, setNextStepId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);

  const { data: carePathData, loading: carePathLoading } = useQuery(LOAD_CAREPLAN_LIST_DATA, {
    variables: {
      listCategory: ['nextStep', 'nextAppointmentType', 'nextAppointmentMethod', 'nextStepTiming'],
      patientId: patientId,
    },
  });

  const { data: latestDraftNextStep, refetch: refetchLatestDraft } = useQuery(GET_LATEST_DRAFT_NEXT_STEP, {
    variables: {
      patientId: patientId,
    },
    fetchPolicy: 'cache-and-network', // Ensures fresh data on initial render
    notifyOnNetworkStatusChange: true, // Notifies on loading state changes
  });

  // Mutation hooks
  const [createOrUpdateNextSteps] = useMutation(CREATE_OR_UPDATE_NEXT_STEPS);
  const [submitNextSteps, { loading: submitLoading }] = useMutation(SUBMIT_NEXT_STEPS, {
    refetchQueries: [
      {
        query: GET_LATEST_DRAFT_NEXT_STEP,
        variables: { patientId: patientId },
      },
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (latestDraftNextStep?.latestDraftNextStep) {
      const draft = latestDraftNextStep.latestDraftNextStep;

      if (draft.status === 'DRAFT') {
        setNextStepId(draft.id);
      }
    }
  }, [latestDraftNextStep]);

  useEffect(() => {
    if (!carePathLoading && carePathData?.carepathDataReferenceList) {
      setNextStepsOptions(filterListdata(carePathData, 'nextStep'));
      setNextAppointmentTypeOptions(filterListdata(carePathData, 'nextAppointmentType'));
      setNextAppointmentMethodOptions(filterListdata(carePathData, 'nextAppointmentMethod'));
      setNextStepTimingOptions(filterListdata(carePathData, 'nextStepTiming'));
    }
  }, [carePathData, carePathLoading]);

  useEffect(() => {
    if (latestDraftNextStep?.latestDraftNextStep) {
      const draft = latestDraftNextStep.latestDraftNextStep;

      // Only load data if status is DRAFT, not SUBMITTED
      if (draft.status === 'DRAFT') {
        setSelectedNextStep(draft.nextStep || '');
        setSelectedNextAppointmentType(draft.nextAppointmentType || '');
        setSelectedNextAppointmentMethod(draft.nextAppointmentMethod || '');
        setSelectedNextStepTiming(draft.timingUnit || '');
        setNextStepTimingInt(draft.timingValue ? String(draft.timingValue) : '');
        setNextStepComments(draft.comments || '');
        return;
      }
    }

    // Clear form if there's no draft or if draft status is not DRAFT
    setSelectedNextStep('');
    setSelectedNextAppointmentType('');
    setSelectedNextAppointmentMethod('');
    setSelectedNextStepTiming('');
    setNextStepTimingInt('');
    setNextStepComments('');
  }, [latestDraftNextStep]);

  const handleSubmitNextStep = async () => {
    if (!selectedNextStep.trim()) {
      setShowError(true);
      return;
    }
    setShowError(false);

    // Try to save the draft first if we have an ID
    let idToSubmit = nextStepId;
    if (nextStepId) {
      idToSubmit = await handleSaveNextStep();
    }

    // Prepare the next step data for submission
    const nextStepData = {
      nextStep: selectedNextStep,
      nextAppointmentType: selectedNextAppointmentType,
      nextAppointmentMethod: selectedNextAppointmentMethod,
      timingValue: parseInt(nextStepTimingInt) || null,
      timingUnit: selectedNextStepTiming,
      comments: nextStepComments,
    };

    submitNextSteps({
      variables: {
        nextStepId: idToSubmit,
        patientId: patientId,
        nextStepData: nextStepData,
      },
    }).then((result) => {
      if (result.data?.submitNextSteps?.success) {
        // Clear form data after successful submission
        setSelectedNextStep('');
        setSelectedNextAppointmentType('');
        setSelectedNextAppointmentMethod('');
        setSelectedNextStepTiming('');
        setNextStepTimingInt('');
        setNextStepComments('');
        setNextStepId(null);

        toggleShowWarningIcon(false);

        setSnackbarOpen(true);

        // Refetch the latest draft to ensure we don't load submitted data
        refetchLatestDraft();
      }
    });
  };

  const handleClearAndSave = async (fieldName: string) => {
    showError && setShowError(false);

    // Determine cleared value based on field name
    const clearedValue = fieldName === 'timingValue' ? null : '';

    const nextStepData = {
      nextStep: selectedNextStep,
      nextAppointmentType: selectedNextAppointmentType,
      nextAppointmentMethod: selectedNextAppointmentMethod,
      timingValue: parseInt(nextStepTimingInt) || null,
      timingUnit: selectedNextStepTiming,
      comments: nextStepComments,
      [fieldName]: clearedValue,
    };

    const variables: any = {
      patientId: patientId,
      nextStepData: nextStepData,
    };

    if (nextStepId) {
      variables.nextStepId = nextStepId;
    }

    await createOrUpdateNextSteps({
      variables,
    });
  };

  const handleSaveNextStep = async () => {
    showError && setShowError(false);

    const nextStepData = {
      nextStep: selectedNextStep,
      nextAppointmentType: selectedNextAppointmentType,
      nextAppointmentMethod: selectedNextAppointmentMethod,
      timingValue: parseInt(nextStepTimingInt) || null,
      timingUnit: selectedNextStepTiming,
      comments: nextStepComments,
    };

    const variables: any = {
      patientId: patientId,
      nextStepData: nextStepData,
    };

    // Only include nextStepId if we have an existing draft
    if (nextStepId) {
      variables.nextStepId = nextStepId;
    }

    const result = await createOrUpdateNextSteps({
      variables,
    });

    // Show warning icon when there's incomplete data
    const hasData =
      selectedNextStep ||
      selectedNextAppointmentType ||
      selectedNextAppointmentMethod ||
      selectedNextStepTiming ||
      nextStepTimingInt ||
      nextStepComments;

    if (hasData) {
      toggleShowWarningIcon(true);
    } else {
      toggleShowWarningIcon(false);
    }

    // If we created a new draft, get the ID from the mutation response
    if (!nextStepId && result.data?.createOrUpdateNextSteps?.nextStep?.id) {
      const newId = result.data.createOrUpdateNextSteps.nextStep.id;
      setNextStepId(newId);
      return newId;
    }

    return nextStepId;
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 200px)',
      }}>
      <form style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack sx={{ flex: 1, paddingTop: '6px', paddingX: '16px' }}>
          <NextStepDropdown
            selectedOption={selectedNextStep}
            setSelectedOption={setSelectedNextStep}
            dropdownOptions={nextStepsOptions}
            label="Next step"
            placeholder="Next step"
            required={true}
            onHandleSave={handleSaveNextStep}
            onClearAndSave={handleClearAndSave}
            fieldName="nextStep"
            showError={showError}
            onClearError={() => setShowError(false)}
            isLoading={carePathLoading}
          />
          <NextStepDropdown
            selectedOption={selectedNextAppointmentType}
            setSelectedOption={setSelectedNextAppointmentType}
            dropdownOptions={nextAppointmentTypeOptions}
            label="Next appointment"
            placeholder="Next appointment type"
            onHandleSave={handleSaveNextStep}
            onClearAndSave={handleClearAndSave}
            fieldName="nextAppointmentType"
            isLoading={carePathLoading}
          />
          <NextStepDropdown
            selectedOption={selectedNextAppointmentMethod}
            setSelectedOption={setSelectedNextAppointmentMethod}
            dropdownOptions={nextAppointmentMethodOptions}
            label="Next appointment method"
            placeholder="Appointment method"
            onHandleSave={handleSaveNextStep}
            onClearAndSave={handleClearAndSave}
            fieldName="nextAppointmentMethod"
            isLoading={carePathLoading}
          />
          <Box sx={{ display: 'flex', marginBottom: '16px', gap: '16px' }}>
            <TextField
              type="number"
              label="Timing"
              value={nextStepTimingInt || ''}
              placeholder="e.g. 3"
              size="small"
              sx={{ width: '30%' }}
              InputLabelProps={{ shrink: true, sx: { color: 'text.secondary' } }}
              inputProps={{ style: { fontSize: 16 }, min: 0 }}
              onChange={(e) => setNextStepTimingInt(e.target.value)}
              onBlur={handleSaveNextStep}
            />
            <TextField
              select
              label="Timing"
              value={selectedNextStepTiming || ''}
              size="small"
              onChange={(e) => setSelectedNextStepTiming(e.target.value)}
              onBlur={handleSaveNextStep}
              InputLabelProps={{ shrink: true, sx: { color: 'text.secondary' } }}
              InputProps={{
                endAdornment: selectedNextStepTiming ? (
                  <ClearButton
                    onClear={() => setSelectedNextStepTiming('')}
                    onClearAndSave={handleClearAndSave}
                    fieldName="timingUnit"
                    useIconButton={false}
                  />
                ) : null,
              }}
              sx={{ width: '70%' }}
              inputProps={{
                'data-testid': 'next-step-timing-select',
                tabIndex: 0,
                style: { fontSize: 16 },
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) {
                    return <Typography sx={{ color: 'text.secondary', fontSize: 16 }}>e.g. Months</Typography>;
                  }
                  return selected as React.ReactNode;
                },
              }}>
              {nextStepTimingOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            multiline
            rows={4}
            label="Comments"
            value={nextStepComments}
            size="small"
            onChange={(e) => setNextStepComments(e.target.value)}
            onBlur={handleSaveNextStep}
            placeholder="Add special instructions (e.g. imaging, duration, location)."
            InputLabelProps={{ shrink: true, sx: { color: 'text.secondary' } }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <Box sx={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <InfoIcon sx={{ width: '20px', height: '20px', color: 'info.dark' }} />
            <Typography sx={{ color: 'info.dark' }}>
              Next steps are final once submitted to ensure care continuity. Please check details carefully.
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ paddingX: '24px', paddingY: '92px', width: '100%', marginTop: 'auto' }}>
          {submitLoading ? (
            <Button
              variant="contained"
              onClick={handleSubmitNextStep}
              disabled={submitLoading}
              sx={{
                marginTop: '8px',
                borderRadius: '4px',
                width: '100%',
                height: '48px',
                color: 'action.disabled',
              }}>
              <CircularProgress size={24} />
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitNextStep}
              disabled={submitLoading}
              sx={{
                marginTop: '8px',
                borderRadius: '4px',
                width: '100%',
                height: '48px',
                textTransform: 'uppercase',
              }}>
              SUBMIT NEXT STEP
            </Button>
          )}
        </Box>
      </form>

      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 800 }}
        message="Next step has been created. "
        key={'Fade'}
        autoHideDuration={10000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        action={
          <IconButton size="small" onClick={() => setSnackbarOpen(false)}>
            <CloseOutlined fontSize="small" sx={{ fill: 'white' }} />
          </IconButton>
        }
      />
    </Box>
  );
};

export default NextSteps;
